import React, { useMemo, useState } from 'react';
import {
  Title,
  Paper,
  Box,
  Loader,
  Alert,
  Card,
  Text,
  Anchor,
  Center,
  Group,
  Stack,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../../hooks/useAuthenticatedData';
import config from '../../../../../config';
import MembershipPopup from '../../MembershipPopup';
import SQLEditorPopup from '../../SQLEditorPopup';

// Simplified Legend component with more concise descriptions
const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder>
    <Title order={6} size="sm" mb={4}>
      Inventory Health Status
    </Title>
    <Stack gap={4}>
      {[
        { color: '#2A7040', label: 'Green: 15% or more ahead of goal' },
        { color: '#A07D32', label: 'Yellow: 1-14% ahead of goal' },
        { color: '#944848', label: 'Red: Behind goal' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

// This function determines the color status for inventory health categories
// Updated to match the simplified legend logic
const getInventoryHealthStatus = (value, ageBucket) => {
  // Goals for each age bucket
  const goals = {
    '0-90': 56,   // At least 56%
    '91-180': 26, // At most 26%
    '181-365': 16, // At most 16%
    '366+': 2     // At most 2%
  };

  const goal = goals[ageBucket];

  // For 0-90 days, higher percentage is better (want minimum 56%)
  if (ageBucket === '0-90') {
    // 15% or more ahead of goal (≥ 64.4%)
    if (value >= goal * 1.15) return 'green';
    // 1-14% ahead of goal (56-64.3%)
    if (value >= goal) return 'yellow';
    // Behind goal (<56%)
    return 'red';
  }
  // For all other buckets, lower percentage is better (want maximum %)
  else {
    // 15% or more ahead of goal (≤ 85% of maximum)
    if (value <= goal * 0.85) return 'green';
    // 1-14% ahead of goal (85-99% of maximum)
    if (value <= goal) return 'yellow';
    // Behind goal (>100% of maximum)
    return 'red';
  }
};

function RepairsDepartmentInventoryHealthDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  // State for technical pop-ups
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';

  const url = `${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/inventory-health-details`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  // Process the data into rows for the table.
  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index, // Ensure each row has a unique id
      // Extract values from the nested structure
      ageCategory: item.values?.agecategory,
      totalCostDollars: item.values?.totalcostdollars,
      percentOfTotalCost: item.values?.percentoftotalcost,
      totalSaleValueDollars: item.values?.totalsalevaluedollars,
      expectedGmDollars: item.values?.expectedgmdollars,
      expectedGmPercent: item.values?.expectedgmpercent,
    }));
  }, [data]);

  // Generate export filename.
  const generateExportFileName = () => `Repairs-inventory-health-details`;

  // Define columns for the table.
  const columns = useMemo(
    () => [
      { accessorKey: 'ageCategory', header: 'Age Category', size: 120 },
      {
        accessorKey: 'totalCostDollars',
        header: 'Total Cost ($)',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'percentOfTotalCost',
        header: '% of Total Cost',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
      {
        accessorKey: 'totalSaleValueDollars',
        header: 'Total Sale Value ($)',
        size: 170,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'expectedGmDollars',
        header: 'Expected GM ($)',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'expectedGmPercent',
        header: 'Expected GM (%)',
        size: 150,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
    ],
    []
  );

  const getRowStyle = (row) => {
    const ageCategory = row.original.ageCategory;
    const percentOfTotalCost = row.original.percentOfTotalCost;

    // Determine which category the row falls into
    let category = '';
    if (ageCategory?.includes('0-90')) {
      category = '0-90';
    } else if (ageCategory?.includes('91-180')) {
      category = '91-180';
    } else if (ageCategory?.includes('181-365')) {
      category = '181-365';
    } else if (ageCategory?.includes('366+')) {
      category = '366+';
    }

    // Apply the color logic
    const status = getInventoryHealthStatus(percentOfTotalCost, category);
    
    if (status === 'green') {
      return {
        backgroundColor: '#2A7040',
        color: 'white',
      };
    }
    if (status === 'yellow') {
      return {
        backgroundColor: '#A07D32',
        color: 'white',
      };
    }
    if (status === 'red') {
      return {
        backgroundColor: '#944848',
        color: 'white',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
  });

  // Dummy function for period tabs (if needed).
  const handleTabChange = (newPeriod) => {
    navigate({
      pathname: location.pathname,
      search: `?period=${newPeriod}`,
    });
  };

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      <Text
        component="h1"
        style={{
          fontFamily: 'Orbitron, sans-serif',
          color: '#FDB813',
          fontStyle: 'italic',
          fontSize: '1.5rem',
          marginBottom: 0,
          textAlign: 'left'
        }}
      >
        Repairs Scorecard - Inventory Health Details
      </Text>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=repairs_dept_inventory_health_summary',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=repairs_dept_inventory_health_details',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="stretch" grow>
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Title order={6} size="sm" mb={4}>
            About the Data
          </Title>
          <Text size="xs" c="dimmed">
            This shows the age of repaired material, the cost to repair it, and the potential value for its sale if it were to sell
            at current Pentagon sell prices. This is not our entire inventory, but rather the inventory we have on shelf that was repaired and has been received.
          </Text>
        </Card>
        <ColorLegend />
      </Group>

      <Paper shadow="sm" p="sm" style={{ height: 400 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>

      {/* Pop-up dialogs */}
      <MembershipPopup opened={openMembership} onClose={() => setOpenMembership(false)} />
      <SQLEditorPopup
        opened={openSummaryQuery}
        onClose={() => setOpenSummaryQuery(false)}
        title="Summary Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/queries/inventory-health-summary-query`}
      />
      <SQLEditorPopup
        opened={openDetailsQuery}
        onClose={() => setOpenDetailsQuery(false)}
        title="Details Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/queries/inventory-health-details-query`}
      />
    </Paper>
  );
}

export default RepairsDepartmentInventoryHealthDetails;